import React from 'react';
import styled, { css } from 'styled-components';
import { ActivityFeed, ActivityItem } from '../../components/ActivityFeed';
import type { ActionList, ActivityEntry } from '../CrmSalesTracking/types';
import { TrackingTabCount } from './shared';

export function formatListDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
}

export function listProgress(list: {
  pos: { initialStatus: string; currentStatus: string }[];
}) {
  let towin = 0,
    active = 0,
    lost = 0,
    prospect = 0;
  list.pos.forEach((p) => {
    if (p.currentStatus === 'towin') towin++;
    else if (p.currentStatus === 'active') active++;
    else if (p.currentStatus === 'lost') lost++;
    else if (p.currentStatus === 'prospect') prospect++;
  });
  return {
    total: list.pos.length,
    toWinTotal: towin + prospect,
    active,
    lost
  };
}

export const SelectionBar = styled.div`
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 14px;
  background: var(--ink, #1a1a1a);
  color: #fff;
  padding: 12px 24px;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.25);
  font-size: 13px;
  z-index: 100;
`;

export const SelectionCount = styled.span`
  strong {
    font-weight: 700;
  }
`;

export const SelectionDivider = styled.div`
  width: 1px;
  height: 20px;
  background: rgba(255, 255, 255, 0.2);
`;

export const SelectionClear = styled.button`
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.7);
  font-size: 12px;
  cursor: pointer;
  &:hover {
    color: #fff;
  }
`;

export const SelectionAction = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  background: var(--white);
  color: var(--ink);
  border: 1px solid rgba(255, 255, 255, 0.18);
  padding: 8px 14px;
  border-radius: 8px;
  font-family: var(--font-sans);
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  transition:
    background 0.15s,
    border-color 0.15s;

  svg {
    width: 14px;
    height: 14px;
  }

  &:hover {
    background: var(--paper);
    border-color: var(--line);
  }
`;

export const SelectionSave = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  background: var(--terracotta);
  color: var(--white);
  border: none;
  padding: 10px 18px;
  border-radius: 8px;
  font-family: var(--font-sans);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition:
    background 0.15s,
    transform 0.15s;
  white-space: nowrap;

  svg {
    width: 15px;
    height: 15px;
  }

  &:hover {
    background: var(--terracotta-dark, #b0411a);
    transform: translateY(-1px);
  }
`;

export const TrackingDrawerOverlay = styled.div<{ $open: boolean }>`
  position: fixed;
  inset: 0;
  background: rgba(43, 61, 84, 0.32);
  z-index: 5990;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.22s ease;

  ${(p) =>
    p.$open &&
    css`
      opacity: 1;
      pointer-events: auto;
    `}
`;

/* Chrome matches proto's base `.drawer`/`.drawer-head`/`.drawer-eyebrow`/
   `.drawer-close`/`.drawer-body` rules, with the Sales Tracking-specific
   `.gv-tracking-drawer { width: 640px }` and `.gv-tracking-tabs--drawer`
   overrides layered on top. */

export const TrackingDrawer = styled.aside<{ $open: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: min(640px, 95vw);
  background: var(--white, #fff);
  border-left: 1px solid var(--line);
  box-shadow: var(--shadow-lg, -8px 0 32px rgba(43, 61, 84, 0.18));
  transform: translateX(100%);
  transition: transform 0.3s cubic-bezier(0.32, 0.72, 0, 1);
  z-index: 6000;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  ${(p) =>
    p.$open &&
    css`
      transform: translateX(0);
    `}
`;

export const TrackingDrawerHead = styled.div`
  padding: 24px 28px 20px;
  border-bottom: 1px solid var(--line);
  flex-shrink: 0;
`;

export const TrackingDrawerEyebrow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: var(--ink-faint);
  font-weight: 700;
`;

export const DrawerCloseBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  color: var(--ink-faint);
  cursor: pointer;
  border-radius: 4px;
  padding: 4px;
  &:hover {
    background: var(--paper, #f9f7f4);
    color: var(--ink);
  }
`;

/* `.gv-tracking-tabs--drawer`: flex row, tabs share width equally, dark
   "active" state with a lighter translucent count pill inside. */

export const TrackingDrawerTabs = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 14px;
`;

export const TrackingDrawerTab = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  flex: 1;
  height: 32px;
  padding: 0 14px;
  border-radius: 999px;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  transition:
    background 0.15s,
    color 0.15s,
    border-color 0.15s;

  svg {
    width: 14px;
    height: 14px;
  }

  ${({ $active }) =>
    $active
      ? css`
          background: var(--ink);
          border: 1px solid var(--ink);
          color: var(--white);

          ${TrackingTabCount} {
            background: rgba(255, 255, 255, 0.18);
            color: var(--white);
          }
        `
      : css`
          background: var(--white);
          border: 1px solid var(--line);
          color: var(--ink-muted);
        `}
`;

export const TrackingDrawerBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px 28px 40px;
`;

export const TrackingEmpty = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 48px 20px;
  text-align: center;
  color: var(--ink-faint);
  font-size: 13px;
`;

/* Pixel-exact copies of CrmSalesTracking's Action List card styling (see
   `pages/CrmSalesTracking/index.tsx`) so proto's requirement that Global
   View reuse "the exact same data and markup as CRM's Action Lists /
   Activity tabs, not a re-implementation" holds for visuals too. */

export const ActionListsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  gap: 16px;
  padding: 20px 28px 28px;
`;

export const ActionListCard = styled.div`
  background: var(--white);
  border: 1px solid var(--line);
  border-radius: 12px;
  padding: 18px 18px 14px;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

export const ActionListCardHead = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
`;

export const ActionListNameRow = styled.div`
  font-family: var(--font-serif);
  font-size: 16px;
  font-weight: 500;
  color: var(--ink);
  line-height: 1.3;
  margin-bottom: 4px;
`;

export const ActionListCardMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  font-size: 11.5px;
  color: var(--ink-faint);
`;

export const MetaDot = styled.span`
  width: 3px;
  height: 3px;
  background: var(--ink-faint);
  border-radius: 50%;
`;

export const PrivacyPill = styled.span<{ $variant: 'private' | 'shared' }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px 2px 6px;
  border-radius: 999px;
  font-size: 10.5px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;

  svg {
    width: 11px;
    height: 11px;
    flex-shrink: 0;
  }

  ${({ $variant }) =>
    $variant === 'private'
      ? `background: var(--paper-warm); color: var(--ink-muted); border: 1px solid var(--line);`
      : `background: rgba(58, 74, 107, 0.10); color: var(--indigo); border: 1px solid rgba(58, 74, 107, 0.18);`}
`;

export const ActionListCountBadge = styled.span`
  background: #2b3d54;
  color: var(--white);
  padding: 5px 10px;
  border-radius: 999px;
  font-size: 11.5px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  flex-shrink: 0;
`;

export const ActionListInfoBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px 14px;
  background: var(--paper);
  border-radius: 8px;
  border-left: 3px solid var(--terracotta);
`;

export const ActionListInfoRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 10px;
  font-size: 12.5px;
  line-height: 1.4;
`;

export const ActionListInfoLabel = styled.span`
  color: var(--ink-faint);
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-weight: 600;
  min-width: 60px;
  flex-shrink: 0;
`;

export const ActionListInfoValue = styled.span`
  color: var(--ink);
  font-weight: 500;
`;

export const SentToType = styled.span`
  color: var(--ink-faint);
  font-weight: 400;
  font-size: 11.5px;
`;

export const ActionListProgressRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 6px;
  border-top: 1px solid var(--line-soft, var(--line));
  padding-top: 12px;
`;

export const actionListToneColors: Record<
  'positive' | 'negative' | 'pending' | 'neutral',
  string
> = {
  positive: 'var(--olive)',
  negative: 'var(--crimson)',
  pending: 'var(--amber)',
  neutral: 'var(--ink-faint)'
};

export const ActionListProgressStat = styled.div<{
  $tone: 'pending' | 'positive' | 'negative' | 'neutral';
}>`
  text-align: center;
  padding: 6px;
  border-radius: 6px;

  strong {
    display: block;
    font-size: 18px;
    font-weight: 600;
    font-family: var(--font-serif);
    font-variant-numeric: tabular-nums;
    line-height: 1;
    color: ${({ $tone }) => actionListToneColors[$tone]};
  }

  span {
    display: block;
    font-size: 10.5px;
    color: var(--ink-faint);
    text-transform: uppercase;
    letter-spacing: 0.06em;
    margin-top: 4px;
    font-weight: 500;
  }
`;

export interface PosTrackingDrawerProps {
  actionLists: ActionList[];
  activity: ActivityEntry[];
  clearTrackingSelection: () => void;
  onSaveActionList: () => void;
  onWriteEmail: () => void;
  setTrackingDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setTrackingDrawerView: React.Dispatch<
    React.SetStateAction<'lists' | 'activity'>
  >;
  trackingDrawerOpen: boolean;
  trackingDrawerView: 'lists' | 'activity';
  trackingOn: boolean;
  trackingSelectedIds: Set<string | number>;
}

export const PosTrackingDrawer: React.FC<PosTrackingDrawerProps> = ({
  actionLists,
  activity,
  clearTrackingSelection,
  onSaveActionList,
  onWriteEmail,
  setTrackingDrawerOpen,
  setTrackingDrawerView,
  trackingDrawerOpen,
  trackingDrawerView,
  trackingOn,
  trackingSelectedIds
}) => (
  <>
    {/* Sales Tracking selection bar + drawer. Previously these were
          portaled to document.body to work around a "fixed element clipped
          at the bottom" bug — but the real root cause was a stray
          `body { zoom: 1; }` reset (Chromium treats any `zoom` on an
          ancestor like a `transform`, creating a new containing block for
          `position: fixed` descendants). That's now removed from
          src/index.css / src/App.css, so these can render inline again —
          which also keeps them inside `.v2-root` and its CSS custom
          properties (the portal was breaking `var(--line)`, `var(--paper)`,
          etc. for any of these styles that didn't have a hardcoded
          fallback). */}
    {trackingOn && trackingSelectedIds.size > 0 && (
      <SelectionBar>
        <SelectionCount>
          <strong>{trackingSelectedIds.size}</strong> POS selected
        </SelectionCount>
        <SelectionDivider />
        <SelectionClear onClick={clearTrackingSelection}>Clear</SelectionClear>
        <SelectionAction type="button" onClick={onWriteEmail}>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z" />
            <polyline points="22 6 12 13 2 6" />
          </svg>
          Write an email
        </SelectionAction>
        <SelectionSave type="button" onClick={onSaveActionList}>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
            <polyline points="17 21 17 13 7 13 7 21" />
            <polyline points="7 3 7 8 15 8" />
          </svg>
          Save as Action List
        </SelectionSave>
      </SelectionBar>
    )}

    <TrackingDrawerOverlay
      $open={trackingDrawerOpen}
      onClick={() => setTrackingDrawerOpen(false)}
    />
    <TrackingDrawer
      $open={trackingDrawerOpen}
      role="dialog"
      aria-modal="false"
      aria-hidden={!trackingDrawerOpen}
    >
      <TrackingDrawerHead>
        <TrackingDrawerEyebrow>
          <span>SALES TRACKING</span>
          <DrawerCloseBtn
            type="button"
            onClick={() => setTrackingDrawerOpen(false)}
            aria-label="Close"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </DrawerCloseBtn>
        </TrackingDrawerEyebrow>
        <TrackingDrawerTabs>
          <TrackingDrawerTab
            type="button"
            $active={trackingDrawerView === 'lists'}
            onClick={() => setTrackingDrawerView('lists')}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M9 11l3 3L22 4" />
              <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
            </svg>
            Action Lists{' '}
            <TrackingTabCount>{actionLists.length}</TrackingTabCount>
          </TrackingDrawerTab>
          <TrackingDrawerTab
            type="button"
            $active={trackingDrawerView === 'activity'}
            onClick={() => setTrackingDrawerView('activity')}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
            </svg>
            Activity <TrackingTabCount>{activity.length}</TrackingTabCount>
          </TrackingDrawerTab>
        </TrackingDrawerTabs>
      </TrackingDrawerHead>
      <TrackingDrawerBody>
        {trackingDrawerView === 'lists' ? (
          actionLists.length === 0 ? (
            <TrackingEmpty>
              <strong>No action lists yet</strong>
              <span>
                Select POS in the Points of Sale tab with Sales Tracking on,
                then click <em>Save as Action List</em>.
              </span>
            </TrackingEmpty>
          ) : (
            <ActionListsGrid>
              {actionLists.map((list) => {
                const prog = listProgress(list);
                return (
                  <ActionListCard key={list.id}>
                    <ActionListCardHead>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <ActionListNameRow>{list.name}</ActionListNameRow>
                        <ActionListCardMeta>
                          <PrivacyPill $variant={list.privacy}>
                            {list.privacy === 'private' ? (
                              <svg
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <rect
                                  x="3"
                                  y="11"
                                  width="18"
                                  height="11"
                                  rx="2"
                                />
                                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                              </svg>
                            ) : (
                              <svg
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                                <circle cx="9" cy="7" r="4" />
                                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                              </svg>
                            )}
                            {list.privacy === 'private' ? 'Private' : 'Shared'}
                          </PrivacyPill>
                          <MetaDot />
                          <span>{formatListDate(list.createdAt)}</span>
                          <MetaDot />
                          <span>by {list.createdBy}</span>
                        </ActionListCardMeta>
                      </div>
                      <ActionListCountBadge>
                        {prog.total} POS
                      </ActionListCountBadge>
                    </ActionListCardHead>
                    <ActionListInfoBox>
                      <ActionListInfoRow>
                        <ActionListInfoLabel>Sent to</ActionListInfoLabel>
                        <ActionListInfoValue>
                          {list.sentTo}{' '}
                          <SentToType>
                            ·{' '}
                            {list.sentToType === 'distributor'
                              ? 'Distributor'
                              : 'Sales team'}
                          </SentToType>
                        </ActionListInfoValue>
                      </ActionListInfoRow>
                      <ActionListInfoRow>
                        <ActionListInfoLabel>Action</ActionListInfoLabel>
                        <ActionListInfoValue>{list.action}</ActionListInfoValue>
                      </ActionListInfoRow>
                    </ActionListInfoBox>
                    <ActionListProgressRow>
                      <ActionListProgressStat $tone="pending">
                        <strong>{prog.toWinTotal}</strong>
                        <span>To Win</span>
                      </ActionListProgressStat>
                      <ActionListProgressStat $tone="positive">
                        <strong>{prog.active}</strong>
                        <span>Active</span>
                      </ActionListProgressStat>
                      <ActionListProgressStat $tone="negative">
                        <strong>{prog.lost}</strong>
                        <span>Lost</span>
                      </ActionListProgressStat>
                    </ActionListProgressRow>
                  </ActionListCard>
                );
              })}
            </ActionListsGrid>
          )
        ) : (
          <ActivityFeed
            items={activity.map(
              (a): ActivityItem => ({
                id: a.id,
                initials: a.userInitials,
                user: a.user,
                kind: a.kind,
                action: a.action,
                target: a.target,
                targetType: a.targetType,
                targetId: a.targetId,
                detail: a.detail,
                when: a.when,
                privacy: a.privacy,
                with_: a.with_,
                posCount: a.posCount,
                sentTo: a.sentTo,
                recipientCount: a.recipientCount
              })
            )}
            onItemClick={(item) => {
              // This panel has no per-list detail view — the closest
              // working "link" is switching to the Action Lists tab where
              // the referenced list is shown.
              if (item.targetType === 'action_list') {
                setTrackingDrawerView('lists');
              }
            }}
          />
        )}
      </TrackingDrawerBody>
    </TrackingDrawer>
  </>
);
